import { Consumer } from 'src/domain/consumer';
import { NextOfKin } from 'src/domain/next-of-kin';
import { OrganizationGroup } from 'src/domain/organization';

export type LeafLabelSuffixOptions = {
    organizationId?: string;
    mainContactsOnly?: boolean;
};

export const buildLeafLabelSuffixByGroupId = (
    consumers?: Consumer[],
    nextOfKins?: NextOfKin[],
    options?: LeafLabelSuffixOptions,
): Record<string, string> | undefined => {
    if (!consumers?.length) {
        return undefined;
    }

    const { organizationId, mainContactsOnly } = options ?? {};
    const map: Record<string, string> = {};

    const getFullName = (firstName?: string, lastName?: string): string =>
        `${firstName?.trim() ?? ''} ${lastName?.trim() ?? ''}`.trim();

    const nextOfKinLookup = new Map<string, string[]>();

    if (nextOfKins?.length) {
        for (const nok of nextOfKins) {
            if (mainContactsOnly && !nok.isMainContact) {
                continue;
            }

            const nokName = getFullName(
                nok.profile.firstName,
                nok.profile.lastName,
            );
            if (!nokName) continue;

            const consumerIds = nok.followers?.consumerIds?.filter(Boolean);
            if (!consumerIds?.length) continue;

            for (const consumerId of consumerIds) {
                const existing = nextOfKinLookup.get(consumerId) ?? [];
                nextOfKinLookup.set(consumerId, [...existing, nokName]);
            }
        }
    }

    for (const consumer of consumers) {
        if (organizationId && consumer.organizationId !== organizationId) {
            continue;
        }

        const fullName = getFullName(
            consumer.profile.firstName,
            consumer.profile.lastName,
        );

        if (!fullName) {
            continue;
        }

        if (!consumer.groupIds?.length) {
            continue;
        }

        const nextOfKinNames = nextOfKinLookup.get(consumer.id);
        const label = nextOfKinNames?.length
            ? `${fullName} (${nextOfKinNames.join(', ')})`
            : fullName;

        for (const groupId of consumer.groupIds) {
            if (!groupId || map[groupId]) continue;

            map[groupId] = label;
        }
    }

    return Object.keys(map).length ? map : undefined;
};

export const buildLeafGroupLabelsById = (
    consumers?: Consumer[],
): Map<string, string> | undefined => {
    if (!consumers?.length) {
        return undefined;
    }

    const map = new Map<string, string>();

    for (const consumer of consumers) {
        const label =
            `${consumer.profile.firstName} ${consumer.profile.lastName}`.trim();
        if (!label) continue;

        for (const groupId of consumer.groupIds ?? []) {
            if (!groupId || map.has(groupId)) continue;
            map.set(groupId, label);
        }
    }

    return map.size ? map : undefined;
};

export const buildParentGroupLabelsByConsumerId = (
    consumers?: Consumer[],
    groups?: OrganizationGroup[],
): Map<string, string> | undefined => {
    if (!consumers?.length || !groups?.length) {
        return undefined;
    }

    const groupsById = new Map(groups.map(group => [group.id, group]));
    const labelsByConsumerId = new Map<string, string>();

    for (const consumer of consumers) {
        const firstGroupId = consumer.groupIds?.[0];
        if (!firstGroupId) continue;

        const childGroup = groupsById.get(firstGroupId);
        if (!childGroup?.parentId) continue;

        const parentGroup = groupsById.get(childGroup.parentId);
        const parentLabel = parentGroup?.name || parentGroup?.id;
        if (!parentLabel) continue;

        labelsByConsumerId.set(consumer.id, parentLabel);
    }

    return labelsByConsumerId.size ? labelsByConsumerId : undefined;
};
